/**
 * App-lock state machine.
 *
 * Decides, at every moment, which of four things the nurse should be looking
 * at: the app, the PIN screen, the setup flow, or the account-ended screen.
 *
 * ─── When it locks ──────────────────────────────────────────────────────────
 *
 * On cold start, and on returning to the foreground after more than
 * `inactivityTimeoutSeconds` in the background (10 minutes, served by the API
 * so it is tunable without an app release).
 *
 * Elapsed BACKGROUND time is the trigger, not idle-while-using. That is the
 * standard behaviour for this class of app and it is the one that matches the
 * actual risk: a phone put down, pocketed, or handed to someone else. A nurse
 * mid-visit with the app open is not a threat, and locking her out while she is
 * documenting would be actively harmful.
 *
 * Time is measured with Date.now() across the background transition rather than
 * a timer, because timers are suspended while backgrounded and would never fire.
 *
 * ─── Backwards compatibility ────────────────────────────────────────────────
 *
 * Everything here is JS and ships over OTA. A nurse on an older build simply
 * never runs this code and is unaffected. Biometrics degrade separately: see
 * isBiometricAvailable in lib/app-lock.ts.
 */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AppState, type AppStateStatus } from "react-native";

import { applyServerResetIfNewer, clearAppLock, clearResetMarker, isPinSet } from "@/lib/app-lock";
import {
    resolveAppLockScreen,
    shouldWipeLocalLock,
    type AppLockScreen
} from "@/lib/app-lock-screen";
import { useAuth } from "@/lib/auth";
import { fetchRnSession, type AppLockPolicy } from "@/lib/session-api";

const DEFAULT_TIMEOUT_SECONDS = 600;

export type { AppLockScreen };

interface AppLockContextValue {
    screen: AppLockScreen;
    /** Null until the first successful session fetch. */
    policy: AppLockPolicy | null;
    /** True once the deadline has passed — setup can no longer be skipped. */
    mustEnroll: boolean;
    accountEndedReason: "terminated" | "deactivated" | null;
    /** Called by the PIN screen after a correct PIN or biometric match. */
    markUnlocked: () => void;
    /** Called by the setup flow once the secret is stored and reported. */
    completeSetup: () => void;
    /** Defers setup during the grace period. */
    skipSetup: () => void;
}

const AppLockContext = createContext<AppLockContextValue | null>(null);

export function AppLockProvider({ children }: { children: React.ReactNode }) {
    // `accountEndedReason` is owned by AuthProvider so that the 403 path in
    // api.ts and the session-endpoint path below converge on one piece of
    // state — otherwise the two could disagree about whether to show the
    // account-ended screen.
    const { isAuthenticated, isLoading, user, accountEndedReason, reportAccountEnded } = useAuth();

    const [policy, setPolicy] = useState<AppLockPolicy | null>(null);
    const [locked, setLocked] = useState(true);
    const [needsSetup, setNeedsSetup] = useState(false);
    const [setupSkipped, setSetupSkipped] = useState(false);
    // Reading the secure store is async, so on cold start we do not yet know
    // whether a PIN exists. Render the cover until we do — otherwise a nurse
    // WITH a PIN gets a frame or two of her actual visit list before the lock
    // screen mounts, which is exactly the exposure this feature exists to stop.
    const [initializing, setInitializing] = useState(true);
    const [hasPin, setHasPin] = useState(false);

    // Wall-clock instant the app last went to background. Null while foregrounded.
    const backgroundedAt = useRef<number | null>(null);
    const timeoutSeconds = useRef(DEFAULT_TIMEOUT_SECONDS);

    // Mirror of `hasPin` for the AppState listener, which is registered once and
    // would otherwise close over the value from its first render.
    const hasPinRef = useRef(false);
    const setHasPinBoth = useCallback((v: boolean) => {
        hasPinRef.current = v;
        setHasPin(v);
    }, []);

    /**
     * Pulls current account standing and lock policy, then reconciles local
     * state with it. Safe to call repeatedly.
     */
    const syncSession = useCallback(async () => {
        if (!isAuthenticated) {
            return;
        }
        try {
            const session = await fetchRnSession();

            if (session.account.blocked) {
                reportAccountEnded(session.account.blockedReason ?? "terminated");
                return;
            }

            const p = session.appLock;
            if (!p) {
                return;
            }
            setPolicy(p);
            timeoutSeconds.current = p.inactivityTimeoutSeconds || DEFAULT_TIMEOUT_SECONDS;

            // An admin reset wipes the local secret and forces re-enrollment.
            const wasReset = await applyServerResetIfNewer(p.resetAt);
            if (wasReset) {
                setSetupSkipped(false);
            }

            const pinSet = await isPinSet();
            setHasPinBoth(pinSet);
            setNeedsSetup(!pinSet);
            // Nothing to unlock into if no secret exists; the setup flow owns
            // the screen until one does.
            if (!pinSet) {
                setLocked(false);
            }
        } catch {
            // Offline or a transient failure. Deliberately non-fatal: we keep
            // whatever policy we already had rather than unlocking, so losing
            // connectivity can never be a way to bypass the lock.
        }
    }, [isAuthenticated, reportAccountEnded, setHasPinBoth]);

    // Cold start: establish whether a PIN exists before deciding what to show.
    useEffect(() => {
        if (!isAuthenticated) {
            setLocked(true);
            setNeedsSetup(false);
            setSetupSkipped(false);
            setPolicy(null);
            setInitializing(false);
            return;
        }
        let cancelled = false;
        setInitializing(true);
        (async () => {
            try {
                const pinSet = await isPinSet();
                if (cancelled) {
                    return;
                }
                setHasPinBoth(pinSet);
                setLocked(pinSet);
                setNeedsSetup(!pinSet);
            } finally {
                // Drop the cover as soon as the LOCAL check resolves. syncSession
                // is a network call and must not gate the first paint, or a nurse
                // on a bad connection stares at a blank screen.
                if (!cancelled) {
                    setInitializing(false);
                }
            }
            await syncSession();
        })();
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, user?.rnId, syncSession, setHasPinBoth]);

    // Foreground/background transitions.
    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }
        const sub = AppState.addEventListener("change", (next: AppStateStatus) => {
            if (next === "background" || next === "inactive") {
                // "inactive" fires for transient interruptions too (control
                // centre, an incoming call, the app switcher). Only stamp the
                // first one so a quick peek does not reset an elapsed timer.
                if (backgroundedAt.current === null) {
                    backgroundedAt.current = Date.now();
                }
                return;
            }
            if (next !== "active") {
                return;
            }
            const since = backgroundedAt.current;
            backgroundedAt.current = null;
            if (since !== null && hasPinRef.current) {
                const elapsedSeconds = (Date.now() - since) / 1000;
                if (elapsedSeconds >= timeoutSeconds.current) {
                    setLocked(true);
                }
            }
            // Re-check standing on every foreground: this is how a termination
            // that happened while the app was closed reaches the device.
            void syncSession();
        });
        return () => sub.remove();
    }, [isAuthenticated, syncSession]);

    const markUnlocked = useCallback(() => {
        setLocked(false);
        backgroundedAt.current = null;
    }, []);

    const completeSetup = useCallback(() => {
        setHasPinBoth(true);
        setNeedsSetup(false);
        setSetupSkipped(false);
        setLocked(false);
        void syncSession();
    }, [syncSession, setHasPinBoth]);

    const skipSetup = useCallback(() => {
        setSetupSkipped(true);
    }, []);

    // Logging out must not leave the next nurse on this device inheriting the
    // previous one's PIN. Wait for the auth restore: the first render of every
    // cold start is unauthenticated, and wiping then deleted the PIN at launch.
    useEffect(() => {
        if (!shouldWipeLocalLock({ isAuthLoading: isLoading, isAuthenticated })) {
            return;
        }
        void clearAppLock();
        void clearResetMarker();
        setHasPinBoth(false);
    }, [isAuthenticated, isLoading, setHasPinBoth]);

    const mustEnroll = policy?.required === true;

    const screen = resolveAppLockScreen({
        accountEndedReason,
        isAuthenticated,
        initializing,
        locked,
        hasPin,
        needsSetup,
        setupSkipped,
        policy
    });

    return (
        <AppLockContext.Provider
            value={{
                screen,
                policy,
                mustEnroll,
                accountEndedReason,
                markUnlocked,
                completeSetup,
                skipSetup
            }}
        >
            {children}
        </AppLockContext.Provider>
    );
}

export function useAppLock(): AppLockContextValue {
    const ctx = useContext(AppLockContext);
    if (!ctx) {
        throw new Error("useAppLock must be used within an AppLockProvider");
    }
    return ctx;
}
